import React, { useState, useEffect, useCallback } from 'react';
import { ChevronUp } from 'lucide-react';
import { blockRegistry } from './blocks';
import WebsiteHeader from './WebsiteHeader';
import WebsiteFooter from './WebsiteFooter';
import { getThemeCSS, getResolvedTheme, buildGoogleFontsUrl } from './themeDefaults';
import { getBlockFontOverrides } from './blocks/common/commonDefaults';
import { getCoachByPlayerProfile } from '../../services/firestoreService';
import { getLibraryItems } from '../../services/libraryService';
import { normalizeAccessLevel, ACCESS_LEVELS } from '../../constants/accessLevels';

/**
 * PreviewMode - Full preview renderer for blocks
 * @param {Array} blocks - Array of block objects to render
 * @param {Array} youtubeVideos - YouTube videos data
 * @param {string} profileId - Profile ID for data fetching
 * @param {string} profileName - Display name for header
 * @param {string} profileLogo - Logo URL for header
 * @param {string} username - Username for building coaching URLs
 * @param {string} accentColor - Theme accent color for components (legacy)
 * @param {object} theme - Full theme object with colors and fonts
 * @param {boolean} footerEnabled - Whether footer is visible
 * @param {string} footerEmail - Email to display in footer
 * @param {string} footerTagline - Tagline to display in footer
 */
const PreviewMode = ({
  blocks,
  youtubeVideos = [],
  photos = [],
  profileId,
  profileName,
  profileLogo,
  username,
  headerNavLinks = [],
  headerNavStyle = 'buttons',
  headerStyle = {},
  accentColor = '#749555',
  theme = {},
  footerEnabled = true,
  footerEmail = '',
  footerTagline = '',
  socialLinks = {},
  hideFooter = false,
  hideHeader = false,
  announcement,
  isOnOwnDomain = false,
  previewContext = null,
  // Site-wide feature flags from BlockEditor — when provided, these override the
  // per-page derivations below so nav items stay visible while previewing pages
  // that don't themselves contain a coaching/library/blog block.
  siteHasCoachingBlock,
  siteHasLibraryContent,
  siteHasBlogPosts,
}) => {
  // Per-page derivations (fallback when site-wide props aren't provided — e.g. ProfileSiteLayout).
  const hasCoachingFromBlocks = blocks.some((b) => b.type === 'coaching');
  const hasCoachingBlock = siteHasCoachingBlock ?? hasCoachingFromBlocks;

  // Fetch coach data once for sharing between WebsiteHeader and CoachingRenderer
  const [coachData, setCoachData] = useState(null);
  const [hasLibraryFromCoach, setHasLibraryFromCoach] = useState(false);
  const hasLibraryContent = siteHasLibraryContent ?? hasLibraryFromCoach;

  useEffect(() => {
    if (hasCoachingBlock && profileId) {
      getCoachByPlayerProfile(profileId)
        .then((coach) => {
          setCoachData(coach);
          if (coach?.id) {
            getLibraryItems(coach.id, null)
              .then((items) => {
                const hasPublic = items.some(item => normalizeAccessLevel(item.accessLevel) !== ACCESS_LEVELS.ADMIN);
                setHasLibraryFromCoach(hasPublic);
              })
              .catch(() => setHasLibraryFromCoach(false));
          }
        })
        .catch(console.error);
    }
  }, [hasCoachingBlock, profileId]);

  // Get resolved theme with all defaults applied
  const resolvedTheme = getResolvedTheme(theme);

  // Get CSS variables for the theme
  const themeCSS = getThemeCSS(theme);

  // Build a single deduplicated Google Fonts URL from theme + header overrides + block overrides
  const fontsUrl = buildGoogleFontsUrl([
    resolvedTheme.fonts.heading,
    resolvedTheme.fonts.body,
    headerStyle?.headingFont,
    headerStyle?.bodyFont,
    ...getBlockFontOverrides(blocks),
  ]);

  if (blocks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <p className="text-gray-400">
          No blocks to preview. Add some blocks in Edit mode.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Load Google Fonts if needed */}
      {fontsUrl && (
        <link
          href={fontsUrl}
          rel="stylesheet"
        />
      )}

      <div
        className="website-preview"
        style={{
          backgroundColor: resolvedTheme.colors.pageBackground,
          width: '100%',
          minWidth: '100%',
          ...themeCSS,
        }}
      >
        {/* Announcement Bar — skip when parent layout (ProfileSiteLayout) already renders it */}
        {!hideHeader && announcement?.enabled && announcement?.text && (
          <AnnouncementBar
            text={announcement.text}
            bgColor={announcement.bgColor}
            textColor={announcement.textColor}
            accentColor={resolvedTheme.colors.accent}
            buttonText={announcement.buttonText}
            buttonLink={announcement.buttonLink}
          />
        )}

        {/* Website Header — skip when parent layout provides it */}
        {!hideHeader && (
          <WebsiteHeader
            profileName={profileName}
            profileLogo={profileLogo}
            username={username}
            hasCoachingBlock={hasCoachingBlock}
            hasLibraryContent={hasLibraryContent}
            headerNavLinks={headerNavLinks}
            headerNavStyle={headerNavStyle}
            headerStyle={headerStyle}
            theme={resolvedTheme}
            coach={coachData}
            previewContext={previewContext}
          />
        )}

        {/* View-only tab placeholders — only for the three feature pages whose
            content is auto-generated from data sources outside the block editor. */}
        {['__coaching', '__library', '__blog'].includes(previewContext?.currentPageSlug) ? (
          <ViewOnlyPreviewPlaceholder
            slug={previewContext.currentPageSlug}
            username={username}
            theme={resolvedTheme}
          />
        ) : (
        <div
          style={{
            fontFamily: `var(--theme-font-body)`,
          }}
        >
          {blocks.map((block) => {
            const BlockRenderer = blockRegistry[block.type]?.Renderer;

            if (!BlockRenderer) {
              return (
                <div
                  key={block.id}
                  id={`block-${block.id}`}
                  className="p-8 text-center"
                  style={{
                    backgroundColor: resolvedTheme.colors.sectionBackground,
                    color: resolvedTheme.colors.mutedColor,
                  }}
                >
                  Unknown block type: {block.type}
                </div>
              );
            }

            return (
              <div key={block.id} id={`block-${block.id}`}>
                <BlockRenderer
                  block={block}
                  youtubeVideos={youtubeVideos}
                  photos={photos}
                  profileId={profileId}
                  username={username}
                  isEmbedded={block.type === 'coaching'}
                  accentColor={resolvedTheme.colors.accent}
                  theme={resolvedTheme}
                  isOnOwnDomain={isOnOwnDomain}
                  {...(block.type === 'coaching' && coachData ? {
                    coach: coachData,
                    coachId: coachData.id,
                    initialUsername: username,
                    initialPlayerProfile: { photoUrl: profileLogo },
                  } : {})}
                />
              </div>
            );
          })}
        </div>
        )}

        {/* Website Footer - rendered if enabled */}
        {!hideFooter && (
          <WebsiteFooter
            footerEmail={footerEmail}
            footerTagline={footerTagline}
            profileName={profileName}
            profileLogo={profileLogo}
            username={username}
            theme={theme}
            socialLinks={socialLinks}
            headerNavLinks={headerNavLinks}
            hasCoachingBlock={hasCoachingBlock}
            hasLibraryContent={hasLibraryContent}
          />
        )}

        {/* Scroll-to-Top Button */}
        <ScrollToTopButton accent={resolvedTheme.colors.accent} />
      </div>
    </>
  );
};

/** Thin announcement strip rendered above the website header */
const AnnouncementBar = ({ text, bgColor, textColor, accentColor, buttonText, buttonLink }) => {
  const bg = bgColor || accentColor || '#749555';

  // Use explicit textColor if set, otherwise auto-detect from bg luminance
  const resolvedTextColor = textColor || (() => {
    if (!bg || !bg.startsWith('#') || bg.length < 7) return '#ffffff';
    const num = parseInt(bg.slice(1), 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#111827' : '#ffffff';
  })();

  return (
    <div
      style={{
        width: '100%',
        backgroundColor: bg,
        color: resolvedTextColor,
        padding: '6px 16px',
        textAlign: 'center',
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {text}
      {buttonText && buttonLink && (
        <>
          {' '}
          <a
            href={buttonLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: resolvedTextColor,
              textDecoration: 'underline',
              fontWeight: 700,
            }}
          >
            {buttonText} &rarr;
          </a>
        </>
      )}
    </div>
  );
};

/** Placeholder card shown in Preview when the user clicks Coaching / Library / Blog
 *  in the mock header. Those pages render live in production from data sources outside
 *  the block editor, so we can't render them inside the preview. We show a clean card
 *  with an "Open Live" action that opens the real page in a new tab. */
const ViewOnlyPreviewPlaceholder = ({ slug, username, theme }) => {
  const config = {
    __coaching: { title: 'Coaching Page', desc: 'This page is auto-generated from your academy settings.', livePath: username ? `/me/${username}/coach` : '/coach', color: theme?.colors?.accent || '#749555' },
    __library: { title: 'Library Page', desc: 'This page is auto-generated from your content library.', livePath: username ? `/me/${username}/library` : '/library', color: '#3b82f6' },
    __blog: { title: 'Blog Page', desc: 'This page shows your published blog posts.', livePath: username ? `/me/${username}/blog` : '/blog', color: '#a855f7' },
  }[slug] || { title: 'Page', desc: 'This page renders live in production.', livePath: '/', color: theme?.colors?.accent || '#749555' };

  return (
    <div style={{ padding: '64px 24px', display: 'flex', justifyContent: 'center' }}>
      <div style={{
        maxWidth: 480,
        width: '100%',
        backgroundColor: theme?.colors?.sectionBackground || '#1a1a2e',
        border: `1px solid ${theme?.colors?.dividerColor || 'rgba(255,255,255,0.1)'}`,
        borderRadius: 12,
        padding: 32,
        textAlign: 'center',
      }}>
        <div style={{
          width: 48, height: 48, margin: '0 auto 16px',
          borderRadius: 10,
          background: `${config.color}33`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <i className="fa-solid fa-eye" style={{ color: config.color, fontSize: 20 }} />
        </div>
        <h3 style={{
          color: theme?.colors?.headingColor || '#fff',
          fontSize: '1.1rem', fontWeight: 600, marginBottom: 8,
        }}>
          {config.title}
        </h3>
        <p style={{
          color: theme?.colors?.mutedColor || '#94a3b8',
          fontSize: '0.875rem', marginBottom: 20,
        }}>
          {config.desc} Preview can't render it inline — open the live version to see it.
        </p>
        <a
          href={config.livePath}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 16px',
            background: config.color, color: '#fff',
            borderRadius: 8, textDecoration: 'none',
            fontSize: '0.875rem', fontWeight: 500,
          }}
        >
          Open Live <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: 12 }} />
        </a>
      </div>
    </div>
  );
};

/** Floating scroll-to-top button — appears after scrolling 400px */
const ScrollToTopButton = ({ accent = '#749555' }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Auto-detect text color from accent luminance
  const textColor = (() => {
    if (!accent || !accent.startsWith('#') || accent.length < 7) return '#ffffff';
    const num = parseInt(accent.slice(1), 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#111827' : '#ffffff';
  })();

  return (
    <button
      onClick={handleClick}
      aria-label="Scroll to top"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 44,
        height: 44,
        borderRadius: '50%',
        backgroundColor: accent,
        color: textColor,
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 35,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <ChevronUp size={22} />
    </button>
  );
};

export default PreviewMode;
